import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import MemberCardModal from '../components/common/MemberCardModal';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmModal from '../components/common/ConfirmModal';
import EmptyState from '../components/common/EmptyState';
import Pagination from '../components/common/Pagination';
import ChurchBadge from '../components/common/ChurchBadge';
import PhoneCheck from '../components/common/PhoneCheck';
import { checkRequired, apiFieldErrors, firstErrorMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';
import { UsersIcon, XIcon } from '../components/common/Icons';
import useIsMobile from '../hooks/useIsMobile';
import useGlobalView from '../hooks/useGlobalView';
import useChurchSettings from '../hooks/useChurchSettings';
import usePermissions from '../hooks/usePermissions';

const GENDER_LABELS = { homme: 'Homme', femme: 'Femme', enfant: 'Enfant' };
const STATUS_LABELS = { nouveau: 'Nouveau', membre: 'Membre régulier', serviteur: 'Serviteur', invite: 'Invité' };
const GENDER_COLORS = {
  homme: { bg: '#eff6ff', color: '#1d4ed8' },
  femme: { bg: '#fdf2f8', color: '#9d174d' },
  enfant: { bg: '#fffbeb', color: '#92400e' },
};
const STATUS_COLORS = {
  nouveau: { bg: '#faf5ff', color: '#6b21a8' },
  membre: { bg: '#f0fdf4', color: '#166534' },
  serviteur: { bg: '#eff6ff', color: '#1e40af' },
  invite: { bg: '#fff7ed', color: '#9a3412' },
};

const MARITAL_LABELS = { celibataire: 'Célibataire', marie: 'Marié(e)', divorce: 'Divorcé(e)', veuf: 'Veuf/Veuve' };

const EMPTY_FORM = {
  first_name: '', last_name: '', gender: 'homme', status: 'membre',
  birth_year: '', birth_date: '',
  phone: '', whatsapp: '', email: '',
  address: '', city: '', neighborhood: '',
  profession: '', marital_status: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  notes: '', family: '', card_template: '',
};

const FIELD_LABELS = {
  first_name: 'Prénom', last_name: 'Nom', gender: 'Genre', status: 'Statut', family: 'Famille',
  birth_date: 'Date de naissance', birth_year: 'Année de naissance', phone: 'Téléphone',
  whatsapp: 'WhatsApp', email: 'Email', card_template: 'Modèle de carte', church_id: 'Église',
};

const styles = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' },
  btnPrimary: {
    backgroundColor: '#1e40af', color: 'white', border: 'none',
    borderRadius: '8px', padding: '10px 20px', cursor: 'pointer',
    fontSize: '0.9rem', fontWeight: 600,
  },
  filters: {
    display: 'flex', gap: '10px', marginBottom: '16px',
    flexWrap: 'wrap', backgroundColor: 'white',
    padding: '14px', borderRadius: '10px',
    border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  searchInput: {
    flex: 1, minWidth: '0', padding: '9px 13px',
    border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '0.9rem',
  },
  select: {
    padding: '9px 12px', border: '1px solid #d1d5db',
    borderRadius: '7px', fontSize: '0.9rem', backgroundColor: 'white', cursor: 'pointer',
  },
  table: {
    width: '100%', backgroundColor: 'white', borderRadius: '12px',
    border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  th: {
    padding: '12px 16px', fontSize: '0.8rem', fontWeight: 700,
    color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
    textAlign: 'left', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '12px 16px', fontSize: '0.9rem', color: '#1e293b',
    borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle',
  },
  badge: { fontSize: '0.75rem', padding: '2px 8px', borderRadius: '999px', fontWeight: 600, display: 'inline-block' },
  rowHover: { cursor: 'pointer' },
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '16px',
  },
  modal: {
    backgroundColor: 'white', borderRadius: '14px',
    padding: '28px', width: '100%', maxWidth: '520px',
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalTitle: { fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', marginBottom: '20px' },
  formGroup: { marginBottom: '13px' },
  formLabel: { display: 'block', fontSize: '0.84rem', fontWeight: 600, color: '#374151', marginBottom: '4px' },
  formInput: {
    width: '100%', padding: '9px 12px', border: '1px solid #d1d5db',
    borderRadius: '7px', fontSize: '0.95rem', color: '#1e293b',
  },
  formRow: { display: 'grid', gap: '12px' },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' },
  btnSecondary: {
    backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db',
    borderRadius: '7px', padding: '9px 18px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
  },
  historyItem: {
    padding: '10px 12px', backgroundColor: '#f8fafc',
    borderRadius: '7px', marginBottom: '6px', border: '1px solid #e2e8f0',
    fontSize: '0.88rem', color: '#374151',
  },
};


export default function Participants({ defaultStatus = '', pageTitle = '' }) {
  const { user } = useAuth();
  const { activeChurchId, isPlatformAdmin } = useChurch();
  const isGlobal = useGlobalView();
  const churchSettings = useChurchSettings(activeChurchId);
  const [families, setFamilies] = useState([]);
  const [cardTemplates, setCardTemplates] = useState([]);
  const mob = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterStatus, setFilterStatus] = useState(defaultStatus);

  // Réinitialiser quand on change de page (participants ↔ fidèles)
  useEffect(() => {
    setFilterStatus(defaultStatus);
    setSearch('');
    setFilterGender('');
  }, [defaultStatus]);
  const [sortBy, setSortBy] = useState('name'); // name | recent | attendance
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [showCardFor, setShowCardFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Edit participant
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  // Reset password
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [resetPwdResult, setResetPwdResult] = useState(null);
  const [resetPwdLoading, setResetPwdLoading] = useState(false);

  const { hasGroupAccess } = usePermissions();
  // Export Excel complet (RCC-EXP-001) : données personnelles, réservé aux administrateurs
  const canExportXlsx = ['admin', 'superviseur', 'superadmin_platform'].includes(user?.role);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState(null);
  useEffect(() => {
    if (!canExportXlsx) return;
    api.get('/participants/duplicates/', { silentError: true })
      .then(res => setDuplicateGroups(res.data.groups.length))
      .catch(() => setDuplicateGroups(null));
  }, [canExportXlsx, activeChurchId]);
  const canEdit = user?.role === 'admin' || user?.role === 'superviseur' || user?.role === 'responsable' || user?.role === 'accueil' || isPlatformAdmin || hasGroupAccess('participants');

  // Ouvrir le modal automatiquement si demandé depuis la sidebar
  useEffect(() => {
    if (!churchSettings.use_families) { setFamilies([]); return; }
    const params = isPlatformAdmin && activeChurchId ? { church_id: activeChurchId } : {};
    api.get('/participants/families/', { params })
      .then(r => setFamilies(Array.isArray(r.data) ? r.data : r.data.results || []))
      .catch(() => setFamilies([]));
  }, [churchSettings.use_families, activeChurchId, isPlatformAdmin]);

  useEffect(() => {
    const params = isPlatformAdmin && activeChurchId ? { church_id: activeChurchId } : {};
    api.get('/cards/templates/', { params })
      .then(r => setCardTemplates(Array.isArray(r.data) ? r.data : r.data.results || []))
      .catch(() => setCardTemplates([]));
  }, [activeChurchId, isPlatformAdmin]);

  useEffect(() => {
    if (sessionStorage.getItem('openCreateParticipant') === '1') {
      sessionStorage.removeItem('openCreateParticipant');
      setForm(EMPTY_FORM);
      setFormErrors({});
      setShowModal(true);
    }
  }, []);

  // Ouvrir le mode édition automatiquement si demandé depuis la page détail d'un participant
  useEffect(() => {
    const editId = sessionStorage.getItem('editParticipantId');
    if (editId) {
      sessionStorage.removeItem('editParticipantId');
      api.get(`/participants/${editId}/`)
        .then(res => {
          setSelectedParticipant(res.data);
          openEdit(res.data);
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const fetchParticipants = useCallback(async () => {
    setLoading(true);
    try {
      const ordering = sortBy === 'recent' ? '-created_at' : 'last_name,first_name';
      let url = `/participants/?ordering=${ordering}&page_size=${PAGE_SIZE}&page=${page}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (filterGender) url += `&gender=${filterGender}`;
      // 'exclude_invite' = filtre spécial pour la page Fidèles (tout sauf invités)
      if (filterStatus === 'exclude_invite') {
        url += `&status=nouveau&status=membre&status=serviteur`;
      } else if (filterStatus) {
        url += `&status=${filterStatus}`;
      }
      const res = await api.get(url);
      let data = Array.isArray(res.data) ? res.data : res.data.results || [];
      if (sortBy === 'attendance') {
        data = [...data].sort((a, b) => (b.attendance_count || 0) - (a.attendance_count || 0));
      }
      setParticipants(data);
      const count = Array.isArray(res.data) ? data.length : (res.data.count ?? data.length);
      setTotalCount(count);
      setPageCount(Math.max(1, Math.ceil(count / PAGE_SIZE)));
    } catch {
      toast.error('Erreur lors du chargement des participants.');
    } finally {
      setLoading(false);
    }
  }, [search, filterGender, filterStatus, sortBy, activeChurchId, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
  }, [search, filterGender, filterStatus, sortBy, activeChurchId]);

  useEffect(() => {
    const timer = setTimeout(() => fetchParticipants(), 300);
    return () => clearTimeout(timer);
  }, [fetchParticipants]);

  const openHistory = async (p) => {
    setSelectedParticipant(p);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/participants/${p.id}/history/`);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const validateForm = () => {
    const errors = checkRequired(form, {
      first_name: 'Prénom', last_name: 'Nom', gender: 'Genre',
      ...(churchSettings.use_families ? { family: 'Famille' } : {}),
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.birth_year) delete payload.birth_year;
      if (!payload.birth_date) delete payload.birth_date;  // une date vide était refusée (400)
      if (!payload.family) delete payload.family;
      if (!payload.card_template) delete payload.card_template;
      // Super admin must pass church_id explicitly
      if (isPlatformAdmin && activeChurchId) payload.church_id = activeChurchId;
      await api.post('/participants/', payload);
      toast.success('Participant ajouté avec succès !');
      setShowModal(false);
      setForm(EMPTY_FORM);
      fetchParticipants();
    } catch (err) {
      const errors = apiFieldErrors(err, FIELD_LABELS);
      setFormErrors(errors);
      toast.error(firstErrorMessage(errors, FIELD_LABELS, "Erreur lors de l'ajout du participant."));
    } finally {
      setSaving(false);
    }
  };

  const deleteParticipant = async (id) => {
    try {
      await api.delete(`/participants/${id}/`);
      toast.success('Participant supprimé.');
      setSelectedParticipant(null);
      setConfirmDelete(null);
      fetchParticipants();
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  const exportParticipantsCSV = () => {
    if (participants.length === 0) return;
    const STATUS_FR = { nouveau: 'Nouveau', membre: 'Membre', serviteur: 'Serviteur', invite: 'Invité' };
    const GENDER_FR = { homme: 'Homme', femme: 'Femme', enfant: 'Enfant' };
    const rows = [
      ['Code membre', 'Prénom', 'Nom', 'Genre', 'Statut', 'Téléphone', 'Email', 'Ville', 'Présences'],
      ...participants.map(p => [
        p.member_code || '', p.first_name || '', p.last_name || '',
        GENDER_FR[p.gender] || p.gender || '',
        STATUS_FR[p.status] || p.status || '',
        p.phone || '', p.email || '', p.city || '',
        p.attendance_count ?? '',
      ]),
    ];
    const bom = '﻿';
    const csv = bom + rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `participants-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === participants.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(participants.map(p => p.id)));
    }
  };

  const bulkDeleteParticipants = async () => {
    try {
      await Promise.all([...selectedIds].map(id => api.delete(`/participants/${id}/`)));
      toast.success(`${selectedIds.size} participant(s) supprimé(s).`);
      setSelectedIds(new Set());
      setConfirmBulkDelete(false);
      fetchParticipants();
    } catch {
      toast.error('Erreur lors de la suppression groupée.');
    }
  };

  const exportSelectedCSV = async () => {
    let toExport;
    if (selectedIds.size > 0) {
      toExport = participants.filter(p => selectedIds.has(p.id));
    } else {
      try {
        const ordering = sortBy === 'recent' ? '-created_at' : 'last_name,first_name';
        let url = `/participants/?ordering=${ordering}&page_size=10000`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (filterGender) url += `&gender=${filterGender}`;
        if (filterStatus === 'exclude_invite') {
          url += `&status=nouveau&status=membre&status=serviteur`;
        } else if (filterStatus) {
          url += `&status=${filterStatus}`;
        }
        const res = await api.get(url);
        toExport = Array.isArray(res.data) ? res.data : res.data.results || [];
      } catch {
        toast.error("Erreur lors de l'export.");
        return;
      }
    }
    if (toExport.length === 0) return;
    const STATUS_FR = { nouveau: 'Nouveau', membre: 'Membre', serviteur: 'Serviteur', invite: 'Invité' };
    const GENDER_FR = { homme: 'Homme', femme: 'Femme', enfant: 'Enfant' };
    const rows = [
      ['Code membre', 'Prénom', 'Nom', 'Genre', 'Statut', 'Téléphone', 'Email', 'Ville', 'Présences', 'Église', 'Famille'],
      ...toExport.map(p => [p.member_code || '', p.first_name || '', p.last_name || '', GENDER_FR[p.gender] || p.gender || '', STATUS_FR[p.status] || p.status || '', p.phone || '', p.email || '', p.city || '', p.attendance_count ?? '', p.church_name || '', p.family_name || '']),
    ];
    const bom = '﻿';
    const csv = bom + rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `participants-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  const exportXlsx = async () => {
    setExportingXlsx(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterGender) params.append('gender', filterGender);
      if (filterStatus === 'exclude_invite') {
        ['nouveau', 'membre', 'serviteur'].forEach(st => params.append('status', st));
      } else if (filterStatus) {
        params.append('status', filterStatus);
      }
      const res = await api.get(`/participants/export-xlsx/?${params.toString()}`, { responseType: 'blob' });
      const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = match ? match[1] : `RCC_MEMBRES_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      if (err.response?.status !== 403 && err.response?.status < 500) toast.error("Erreur lors de l'export Excel.");
    } finally {
      setExportingXlsx(false);
    }
  };

  const openEdit = (p) => {
    setEditForm({
      first_name: p.first_name || '', last_name: p.last_name || '',
      gender: p.gender || 'homme', status: p.status || 'membre',
      birth_date: p.birth_date || '', birth_year: p.birth_year || '',
      phone: p.phone || '', whatsapp: p.whatsapp || '', email: p.email || '',
      address: p.address || '', city: p.city || '', neighborhood: p.neighborhood || '',
      profession: p.profession || '', marital_status: p.marital_status || '',
      emergency_contact_name: p.emergency_contact_name || '',
      emergency_contact_phone: p.emergency_contact_phone || '',
      notes: p.notes || '', family: p.family || '', card_template: p.card_template || '',
    });
    setEditErrors({});
    setEditMode(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    const errors = checkRequired(editForm, { first_name: 'Prénom', last_name: 'Nom', gender: 'Genre' });
    setEditErrors(errors);
    if (Object.keys(errors).length) return;
    setEditSaving(true);
    try {
      const payload = { ...editForm };
      if (!payload.birth_year) delete payload.birth_year;
      if (!payload.birth_date) delete payload.birth_date;
      if (!payload.family) payload.family = null;
      if (!payload.card_template) payload.card_template = null;
      const res = await api.patch(`/participants/${selectedParticipant.id}/`, payload);
      toast.success('Modifications enregistrées.');
      const updated = { ...selectedParticipant, ...res.data };
      setSelectedParticipant(updated);
      setEditMode(false);
      fetchParticipants();
    } catch (err) {
      const errors = apiFieldErrors(err, FIELD_LABELS);
      setEditErrors(errors);
      toast.error(firstErrorMessage(errors, FIELD_LABELS, 'Erreur lors de la sauvegarde.'));
    } finally { setEditSaving(false); }
  };

  const handleResetPassword = async (action = 'reset') => {
    setResetPwdLoading(true);
    setResetPwdResult(null);
    try {
      const res = await api.post(
        `/notifications/admin/reset-password/${selectedParticipant.id}/`,
        { action }
      );
      setResetPwdResult({ success: true, ...res.data });
      toast.success(res.data.message);
    } catch (err) {
      setResetPwdResult({ success: false, error: err.response?.data?.error || 'Erreur' });
      toast.error(err.response?.data?.error || 'Erreur lors de la réinitialisation.');
    } finally { setResetPwdLoading(false); }
  };

  return (
    <div style={styles.page}>
      <ConfirmModal
        open={!!confirmDelete}
        title="Supprimer ce participant ?"
        message={`Cette action est irréversible. ${confirmDelete?.first_name} ${confirmDelete?.last_name} (${confirmDelete?.member_code}) sera définitivement supprimé.`}
        onConfirm={() => deleteParticipant(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
      <ConfirmModal
        open={confirmBulkDelete}
        title={`Supprimer ${selectedIds.size} participant(s) ?`}
        message="Cette action est irréversible. Tous les participants sélectionnés seront définitivement supprimés."
        onConfirm={bulkDeleteParticipants}
        onCancel={() => setConfirmBulkDelete(false)}
      />

      <div style={styles.header}>
        <h1 style={styles.title}>{pageTitle || 'Participants'} ({participants.length})</h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {participants.length > 0 && (
            <button onClick={exportSelectedCSV}
              style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 16px', backgroundColor: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700, color: '#16a34a' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {selectedIds.size > 0 ? `Export (${selectedIds.size})` : 'Export CSV'}
            </button>
          )}
          {canExportXlsx && (
            <button onClick={() => navigate('/doublons')}
              title="Fiches qui semblent correspondre à la même personne : fusionner ou supprimer"
              style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 16px', backgroundColor: duplicateGroups ? '#fef2f2' : '#f8fafc', border: `1.5px solid ${duplicateGroups ? '#fecaca' : '#e2e8f0'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700, color: duplicateGroups ? '#b91c1c' : '#475569' }}>
              Doublons{duplicateGroups !== null ? ` (${duplicateGroups})` : ''}
            </button>
          )}
          {participants.length > 0 && canExportXlsx && (
            <button onClick={exportXlsx} disabled={exportingXlsx}
              title="Toutes les informations des fidèles, avec les filtres en cours"
              style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 16px', backgroundColor: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: '8px', cursor: exportingXlsx ? 'default' : 'pointer', fontSize: '0.88rem', fontWeight: 700, color: '#1e40af', opacity: exportingXlsx ? 0.7 : 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {exportingXlsx ? 'Export...' : 'Export Excel complet'}
            </button>
          )}
          {canEdit && (
            <button style={styles.btnPrimary} onClick={() => { setForm(EMPTY_FORM); setFormErrors({}); setShowModal(true); }}>
              + Ajouter un participant
            </button>
          )}
        </div>
      </div>

      <div style={styles.filters}>
        {/* Ligne 1 : recherche */}
        <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input style={{ ...styles.searchInput, paddingLeft: '33px' }}
              placeholder="Rechercher par nom, prénom ou téléphone..."
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </div>
          {(search || filterGender || filterStatus || sortBy !== 'name') && (
            <button style={{ ...styles.btnSecondary, padding: '9px 14px', backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', whiteSpace: 'nowrap', fontWeight: 700 }}
              onClick={() => { setSearch(''); setFilterGender(''); setFilterStatus(''); setSortBy('name'); }}>
              ✕ Réinitialiser
            </button>
          )}
        </div>
        {/* Ligne 2 : filtres + tri */}
        <div style={{ width: '100%', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={styles.select} value={filterGender} onChange={e => setFilterGender(e.target.value)}>
            <option value="">Tous les genres</option>
            {Object.entries(GENDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select style={styles.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select style={{ ...styles.select, borderColor: sortBy !== 'name' ? '#1e40af' : '#d1d5db', color: sortBy !== 'name' ? '#1e40af' : '#374151' }}
            value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="name">Trier : A → Z</option>
            <option value="recent">Trier : Plus récents</option>
            <option value="attendance">Trier : Plus de présences</option>
          </select>
          {!loading && (
            <span style={{ marginLeft: 'auto', fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
              {participants.length} participant{participants.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <LoadingSpinner size={36} label="Chargement..." />
        </div>
      ) : participants.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <EmptyState icon="participants" title="Aucun participant trouvé" description={search || filterGender || filterStatus ? "Essayez de modifier vos filtres de recherche." : "Commencez par ajouter un fidèle."} action={!search && !filterGender && !filterStatus ? () => setShowModal(true) : null} actionLabel="+ Ajouter un fidèle" />
        </div>
      ) : (
        <>
          {/* Barre d'actions groupées */}
          {selectedIds.size > 0 && canEdit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', background: '#eff6ff', borderRadius: '10px', border: '1.5px solid #bfdbfe', marginBottom: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.9rem' }}>{selectedIds.size} sélectionné(s)</span>
              <button onClick={exportSelectedCSV} style={{ padding: '6px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Exporter
              </button>
              <button onClick={() => setConfirmBulkDelete(true)} style={{ padding: '6px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                Supprimer
              </button>
              <button onClick={() => setSelectedIds(new Set())} style={{ marginLeft: 'auto', padding: '6px 12px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', fontWeight: 600, fontSize: '0.82rem' }}>✕ Annuler</button>
            </div>
          )}
        <div style={{ ...styles.table, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: mob ? '480px' : 'auto' }}>
            <thead>
              <tr>
                {canEdit && <th style={{ ...styles.th, width: '40px', padding: '12px 8px 12px 16px' }}><input type="checkbox" checked={selectedIds.size === participants.length && participants.length > 0} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} /></th>}
                <th style={styles.th}>Nom</th>
                {!mob && <th style={styles.th}>Code</th>}
                {!mob && <th style={styles.th}>Genre</th>}
                <th style={styles.th}>Statut</th>
                {!mob && <th style={styles.th}>Téléphone</th>}
                {isGlobal && <th style={styles.th}>Église</th>}
                {!mob && <th style={styles.th}>Famille</th>}
                {!mob && <th style={styles.th}>Ville</th>}
                {!mob && <th style={styles.th}>Inscrit le</th>}
                <th style={styles.th}>Présences</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((p, idx) => {
                const gc = GENDER_COLORS[p.gender] || {};
                const sc = STATUS_COLORS[p.status] || {};
                const isSelected = selectedIds.has(p.id);
                return (
                  <tr
                    key={p.id}
                    style={{ ...styles.rowHover, backgroundColor: isSelected ? '#eff6ff' : idx % 2 === 0 ? 'white' : '#fafafa' }}
                    onClick={() => navigate(`/participants/${p.id}`)}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f0f9ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : idx % 2 === 0 ? 'white' : '#fafafa'; }}
                  >
                    {canEdit && <td style={{ ...styles.td, padding: '12px 8px 12px 16px', width: '40px' }} onClick={e => toggleSelect(p.id, e)}><input type="checkbox" checked={isSelected} onChange={() => {}} style={{ cursor: 'pointer' }} /></td>}
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600 }}>{p.full_name}</div>
                      {mob && (
                        <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                          <span style={{ ...styles.badge, backgroundColor: gc.bg, color: gc.color, fontSize: '0.7rem' }}>{GENDER_LABELS[p.gender] || p.gender}</span>
                          {p.phone && <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.phone}</span>}
                          {p.family_name && <span style={{ fontSize: '0.72rem', color: '#64748b' }}>· {p.family_name}</span>}
                        </div>
                      )}
                    </td>
                    {!mob && <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.82rem', color: '#475569', whiteSpace: 'nowrap' }}>{p.member_code}</td>}
                    {!mob && <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: gc.bg, color: gc.color }}>
                        {GENDER_LABELS[p.gender] || p.gender}
                      </span>
                    </td>}
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: sc.bg, color: sc.color }}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    </td>
                    {!mob && <td style={styles.td}>{p.phone || '—'}</td>}
                    {isGlobal && <td style={styles.td}><ChurchBadge name={p.church_name} /></td>}
                    {!mob && <td style={styles.td}>{p.family_name || <span style={{ color: '#94a3b8' }}>Sans famille</span>}</td>}
                    {!mob && <td style={styles.td}>{p.city || '—'}</td>}
                    {!mob && <td style={{ ...styles.td, whiteSpace: 'nowrap', color: '#64748b' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '—'}</td>}
                    <td style={{ ...styles.td, fontWeight: 700, color: '#1e40af' }}>{p.attendance_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageCount={pageCount} totalCount={totalCount} pageSize={PAGE_SIZE} onChange={setPage} />
        </>
      )}

      {/* Add Participant Modal */}
      {showModal && (
        <div style={{ ...styles.overlay, alignItems: mob ? 'flex-end' : 'center', padding: mob ? 0 : '16px' }} onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ ...styles.modal, maxWidth: mob ? '100%' : '640px', borderRadius: mob ? '20px 20px 0 0' : '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={styles.modalTitle}>Ajouter un fidèle</div>
              <button style={{ ...styles.btnSecondary, padding: '5px 10px', display: 'flex', alignItems: 'center' }} onClick={() => setShowModal(false)}><XIcon size={16} color="#374151" /></button>
            </div>
            <form onSubmit={handleSubmit} noValidate>

              {/* Section Identité */}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #eff6ff' }}>Identité</div>
              <div style={{ ...styles.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Prénom<Req /></label>
                  <input style={{ ...styles.formInput, borderColor: formErrors.first_name ? ERROR_COLOR : '#d1d5db' }}
                    value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" />
                  <FieldError message={formErrors.first_name} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Nom<Req /></label>
                  <input style={{ ...styles.formInput, borderColor: formErrors.last_name ? ERROR_COLOR : '#d1d5db' }}
                    value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom de famille" />
                  <FieldError message={formErrors.last_name} />
                </div>
              </div>
              <div style={{ ...styles.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Genre<Req /></label>
                  <select style={{ ...styles.formInput, borderColor: formErrors.gender ? ERROR_COLOR : '#d1d5db' }}
                    value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                    {Object.entries(GENDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <FieldError message={formErrors.gender} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Statut à l'église</label>
                  <select style={styles.formInput} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              {churchSettings.use_families && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Famille<Req /></label>
                  <select style={{ ...styles.formInput, borderColor: formErrors.family ? ERROR_COLOR : '#d1d5db' }}
                    value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value })}>
                    <option value="">— Sélectionner —</option>
                    {families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <FieldError message={formErrors.family} />
                </div>
              )}
              {cardTemplates.length >= 1 && (
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Modèle de carte de membre</label>
                  <select style={styles.formInput} value={form.card_template || ''} onChange={(e) => setForm({ ...form, card_template: e.target.value })}>
                    <option value="">— Modèle par défaut de l'église —</option>
                    {cardTemplates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (défaut)' : ''}</option>)}
                  </select>
                </div>
              )}
              <div style={{ ...styles.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Date de naissance</label>
                  <input type="date" style={styles.formInput} value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Situation familiale</label>
                  <select style={styles.formInput} value={form.marital_status} onChange={(e) => setForm({ ...form, marital_status: e.target.value })}>
                    <option value="">— Sélectionner —</option>
                    {Object.entries(MARITAL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Profession / Métier</label>
                <input style={styles.formInput} value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder="Ex: Enseignant, Ingénieur, Étudiant..." />
              </div>

              {/* Section Contact */}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '16px 0 10px', paddingBottom: '6px', borderBottom: '2px solid #eff6ff' }}>Contact</div>
              <div style={{ ...styles.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Téléphone</label>
                  <input style={styles.formInput} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Ex: +237 6XX XXX XXX" />
                  <PhoneCheck phone={form.phone} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>WhatsApp</label>
                  <input style={styles.formInput} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="Si différent du tél." />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Email</label>
                <input type="email" style={styles.formInput} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="adresse@email.com" />
              </div>

              {/* Section Adresse */}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '16px 0 10px', paddingBottom: '6px', borderBottom: '2px solid #eff6ff' }}>Adresse</div>
              <div style={{ ...styles.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Ville</label>
                  <input style={styles.formInput} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ex: Yaoundé" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Quartier</label>
                  <input style={styles.formInput} value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} placeholder="Ex: Bastos, Melen..." />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Adresse complète</label>
                <input style={styles.formInput} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Rue, numéro..." />
              </div>

              {/* Section Urgence */}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '16px 0 10px', paddingBottom: '6px', borderBottom: '2px solid #eff6ff' }}>Contact d'urgence</div>
              <div style={{ ...styles.formRow, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Nom du contact</label>
                  <input style={styles.formInput} value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="Nom complet" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Téléphone du contact</label>
                  <input style={styles.formInput} value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} placeholder="+237 6XX XXX XXX" />
                </div>
              </div>

              {/* Notes */}
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '16px 0 10px', paddingBottom: '6px', borderBottom: '2px solid #eff6ff' }}>Notes</div>
              <div style={styles.formGroup}>
                <textarea style={{ ...styles.formInput, height: '72px', resize: 'vertical' }}
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Informations complémentaires, besoins pastoraux..." />
              </div>

              <div style={styles.modalActions}>
                <button type="button" style={styles.btnSecondary} onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>{saving ? 'Enregistrement...' : 'Ajouter le fidèle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Participant Detail / Edit Modal */}
      {selectedParticipant && (
        <div style={{ ...styles.overlay, alignItems: mob ? 'flex-end' : 'center', padding: mob ? 0 : '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setSelectedParticipant(null); setEditMode(false); setShowResetPwd(false); setResetPwdResult(null); } }}>
          <div style={{ ...styles.modal, maxWidth: mob ? '100%' : '600px', borderRadius: mob ? '20px 20px 0 0' : '14px', maxHeight: '92vh', overflowY: 'auto' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <div>
                <div style={styles.modalTitle}>{selectedParticipant.full_name}</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#94a3b8', marginTop: '2px' }}>{selectedParticipant.member_code}</div>
              </div>
              <button style={{ ...styles.btnSecondary, padding: '6px 10px', display: 'flex', alignItems: 'center' }}
                onClick={() => { setSelectedParticipant(null); setEditMode(false); setShowResetPwd(false); setResetPwdResult(null); }}>
                <XIcon size={16} color="#374151" />
              </button>
            </div>

            {/* Tab bar: Infos | Modifier | Sécurité */}
            {canEdit && (
              <div style={{ display: 'flex', gap: '3px', margin: '14px 0', backgroundColor: '#f1f5f9', borderRadius: '9px', padding: '3px' }}>
                {[
                  { key: 'view', label: 'Informations' },
                  { key: 'edit', label: 'Modifier le profil' },
                  { key: 'security', label: 'Sécurité' },
                ].map(t => (
                  <button key={t.key}
                    onClick={() => { if (t.key === 'edit') openEdit(selectedParticipant); else setEditMode(false); setShowResetPwd(t.key === 'security'); setResetPwdResult(null); }}
                    style={{ flex: 1, padding: '8px 6px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                      backgroundColor: (t.key === 'edit' && editMode) || (t.key === 'security' && showResetPwd) || (t.key === 'view' && !editMode && !showResetPwd) ? 'white' : 'transparent',
                      color: (t.key === 'edit' && editMode) || (t.key === 'security' && showResetPwd) || (t.key === 'view' && !editMode && !showResetPwd) ? '#1e40af' : '#64748b',
                      boxShadow: (t.key === 'edit' && editMode) || (t.key === 'security' && showResetPwd) || (t.key === 'view' && !editMode && !showResetPwd) ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {/* ── TAB: Informations (view) ── */}
            {!editMode && !showResetPwd && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                  <span style={{ backgroundColor: '#eff6ff', color: '#1e40af', padding: '2px 8px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600 }}>{GENDER_LABELS[selectedParticipant.gender]}</span>
                  <span style={{ backgroundColor: '#f0fdf4', color: '#166534', padding: '2px 8px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600 }}>{STATUS_LABELS[selectedParticipant.status]}</span>
                  {selectedParticipant.marital_status && <span style={{ backgroundColor: '#faf5ff', color: '#7e22ce', padding: '2px 8px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600 }}>{MARITAL_LABELS[selectedParticipant.marital_status]}</span>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '8px', marginBottom: '14px', fontSize: '0.83rem' }}>
                  {selectedParticipant.phone && <div><span style={{ color: '#94a3b8', fontWeight: 600 }}>Tél : </span>{selectedParticipant.phone}</div>}
                  {selectedParticipant.whatsapp && <div><span style={{ color: '#94a3b8', fontWeight: 600 }}>WhatsApp : </span>{selectedParticipant.whatsapp}</div>}
                  {selectedParticipant.email && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>Email : </span>{selectedParticipant.email}</div>}
                  {selectedParticipant.birth_date && <div><span style={{ color: '#94a3b8', fontWeight: 600 }}>Naissance : </span>{selectedParticipant.birth_date}</div>}
                  {selectedParticipant.profession && <div><span style={{ color: '#94a3b8', fontWeight: 600 }}>Profession : </span>{selectedParticipant.profession}</div>}
                  {selectedParticipant.city && <div><span style={{ color: '#94a3b8', fontWeight: 600 }}>Ville : </span>{selectedParticipant.city}{selectedParticipant.neighborhood ? ` · ${selectedParticipant.neighborhood}` : ''}</div>}
                  {selectedParticipant.emergency_contact_name && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>Urgence : </span>{selectedParticipant.emergency_contact_name}{selectedParticipant.emergency_contact_phone ? ` — ${selectedParticipant.emergency_contact_phone}` : ''}</div>}
                  {selectedParticipant.notes && <div style={{ gridColumn: '1/-1', fontStyle: 'italic', color: '#64748b' }}>{selectedParticipant.notes}</div>}
                </div>

                <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: '10px' }}>
                  Historique des présences ({selectedParticipant.attendance_count})
                </div>
                {historyLoading ? <LoadingSpinner size={24} /> : history.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Aucune présence enregistrée.</p>
                ) : (
                  history.map((h) => (
                    <div key={h.id} style={styles.historyItem}>
                      <strong>{h.activity_name}</strong>
                      <span style={{ float: 'right', color: '#64748b' }}>{h.activity_date}</span>
                      {h.activity_type && <div style={{ color: '#1e40af', fontSize: '0.8rem' }}>{h.activity_type}</div>}
                    </div>
                  ))
                )}
                {selectedParticipant.member_code && (
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <button onClick={() => setShowCardFor(selectedParticipant.id)}
                      style={{ background: '#1e293b', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                      Voir la carte de membre
                    </button>
                  </div>
                )}
                {['admin', 'superadmin_platform', 'superviseur', 'responsable'].includes(user?.role) && (
                  <div style={{ marginTop: '16px', textAlign: 'right' }}>
                    <button style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '7px', padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                      onClick={() => setConfirmDelete(selectedParticipant)}>
                      Supprimer ce participant
                    </button>
                  </div>
                )}
              </>
            )}

            {/* ── TAB: Modifier le profil ── */}
            {editMode && (
              <form onSubmit={handleEditSave} noValidate>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #eff6ff' }}>Identité</div>
                <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '11px', marginBottom: '11px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Prénom<Req /></label>
                    <input style={{ ...styles.formInput, borderColor: editErrors.first_name ? ERROR_COLOR : '#d1d5db' }} value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} />
                    <FieldError message={editErrors.first_name} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Nom<Req /></label>
                    <input style={{ ...styles.formInput, borderColor: editErrors.last_name ? ERROR_COLOR : '#d1d5db' }} value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} />
                    <FieldError message={editErrors.last_name} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Genre<Req /></label>
                    <select style={styles.formInput} value={editForm.gender} onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}>
                      {Object.entries(GENDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Statut</label>
                    <select style={styles.formInput} value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  {churchSettings.use_families && (
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Famille</label>
                      <select style={styles.formInput} value={editForm.family || ''} onChange={e => setEditForm(f => ({ ...f, family: e.target.value }))}>
                        <option value="">— Aucune —</option>
                        {families.map(fam => <option key={fam.id} value={fam.id}>{fam.name}</option>)}
                      </select>
                    </div>
                  )}
                  {cardTemplates.length >= 1 && (
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Modèle de carte de membre</label>
                      <select style={styles.formInput} value={editForm.card_template || ''} onChange={e => setEditForm(f => ({ ...f, card_template: e.target.value }))}>
                        <option value="">— Modèle par défaut de l'église —</option>
                        {cardTemplates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (défaut)' : ''}</option>)}
                      </select>
                    </div>
                  )}
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Date de naissance</label>
                    <input type="date" style={styles.formInput} value={editForm.birth_date} onChange={e => setEditForm(f => ({ ...f, birth_date: e.target.value }))} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Situation familiale</label>
                    <select style={styles.formInput} value={editForm.marital_status} onChange={e => setEditForm(f => ({ ...f, marital_status: e.target.value }))}>
                      <option value="">— Sélectionner —</option>
                      {Object.entries(MARITAL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #eff6ff' }}>Coordonnées</div>
                <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '11px', marginBottom: '11px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Téléphone</label>
                    <input style={styles.formInput} value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder="6XX XXX XXX" />
                    {editForm.phone !== (selectedParticipant?.phone || '') && <PhoneCheck phone={editForm.phone} excludeId={selectedParticipant?.id} />}
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>WhatsApp</label>
                    <input style={styles.formInput} value={editForm.whatsapp} onChange={e => setEditForm(f => ({ ...f, whatsapp: e.target.value }))} />
                  </div>
                  <div style={{ ...styles.formGroup, gridColumn: '1/-1' }}>
                    <label style={styles.formLabel}>Email</label>
                    <input type="email" style={styles.formInput} value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Ville</label>
                    <input style={styles.formInput} value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Quartier</label>
                    <input style={styles.formInput} value={editForm.neighborhood} onChange={e => setEditForm(f => ({ ...f, neighborhood: e.target.value }))} />
                  </div>
                  <div style={{ ...styles.formGroup, gridColumn: '1/-1' }}>
                    <label style={styles.formLabel}>Adresse</label>
                    <input style={styles.formInput} value={editForm.address} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} />
                  </div>
                </div>

                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #eff6ff' }}>Autres infos</div>
                <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '11px', marginBottom: '11px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Profession</label>
                    <input style={styles.formInput} value={editForm.profession} onChange={e => setEditForm(f => ({ ...f, profession: e.target.value }))} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Contact urgence (nom)</label>
                    <input style={styles.formInput} value={editForm.emergency_contact_name} onChange={e => setEditForm(f => ({ ...f, emergency_contact_name: e.target.value }))} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Contact urgence (tél)</label>
                    <input style={styles.formInput} value={editForm.emergency_contact_phone} onChange={e => setEditForm(f => ({ ...f, emergency_contact_phone: e.target.value }))} />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Notes internes</label>
                  <textarea style={{ ...styles.formInput, minHeight: '70px', resize: 'vertical' }} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
                </div>

                <div style={{ display: 'flex', gap: '9px', justifyContent: 'flex-end', marginTop: '6px' }}>
                  <button type="button" style={styles.btnSecondary} onClick={() => setEditMode(false)}>Annuler</button>
                  <button type="submit" style={styles.btnPrimary} disabled={editSaving}>
                    {editSaving ? 'Sauvegarde...' : 'Enregistrer les modifications'}
                  </button>
                </div>
              </form>
            )}

            {/* ── TAB: Sécurité (reset password) ── */}
            {showResetPwd && (
              <div>
                <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '18px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', marginBottom: '4px' }}>Mot de passe portail fidèle</div>
                  <div style={{ fontSize: '0.83rem', color: '#64748b', lineHeight: 1.5 }}>
                    Réinitialisez le mot de passe d'accès au portail fidèle de <strong>{selectedParticipant.first_name}</strong>.
                    Un nouveau mot de passe aléatoire sera généré.
                    {selectedParticipant.email
                      ? <span style={{ color: '#16a34a' }}> Il sera envoyé par email à <strong>{selectedParticipant.email}</strong> si la config email est active.</span>
                      : <span style={{ color: '#f59e0b' }}> Aucun email renseigné — communiquez le mot de passe manuellement.</span>
                    }
                  </div>
                </div>

                {resetPwdResult && (
                  <div style={{ backgroundColor: resetPwdResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${resetPwdResult.success ? '#bbf7d0' : '#fecaca'}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
                    {resetPwdResult.success ? (
                      <>
                        <div style={{ fontWeight: 700, color: '#15803d', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> {resetPwdResult.message}</div>
                        {resetPwdResult.email_sent
                          ? <div style={{ fontSize: '0.78rem', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '5px' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> Email envoyé à {selectedParticipant.email}</div>
                          : <>
                              <div style={{ fontSize: '0.85rem', color: '#166534', marginBottom: '6px' }}>
                                Nouveau mot de passe :{' '}
                                <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.05rem', backgroundColor: 'white', padding: '2px 8px', borderRadius: '5px', border: '1px solid #bbf7d0', letterSpacing: '0.1em' }}>
                                  {resetPwdResult.new_password}
                                </span>
                              </div>
                              <div style={{ fontSize: '0.78rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: '5px' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> Communiquez ce mot de passe manuellement au membre.</div>
                            </>
                        }
                      </>
                    ) : (
                      <div style={{ color: '#b91c1c', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> {resetPwdResult.error}</div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    style={{ ...styles.btnPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', opacity: resetPwdLoading ? 0.7 : 1 }}
                    onClick={() => handleResetPassword('reset')} disabled={resetPwdLoading}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    {resetPwdLoading ? 'Réinitialisation...' : 'Générer un nouveau mot de passe'}
                  </button>
                  <button
                    style={{ backgroundColor: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: '8px', padding: '11px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', opacity: resetPwdLoading ? 0.7 : 1 }}
                    onClick={() => handleResetPassword('clear')} disabled={resetPwdLoading}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    Supprimer le mot de passe (accès libre)
                  </button>
                  <button
                    style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '11px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', opacity: resetPwdLoading ? 0.7 : 1 }}
                    onClick={() => handleResetPassword('unlock')} disabled={resetPwdLoading}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                    Débloquer la connexion (trop de tentatives)
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '12px', lineHeight: 1.5 }}>
                  "Accès libre" signifie que le membre pourra se connecter avec son code membre seul, sans mot de passe.
                </p>
              </div>
            )}

          </div>
        </div>
      )}
      {showCardFor && <MemberCardModal participantId={showCardFor} onClose={() => setShowCardFor(null)} />}
    </div>
  );
}
